/** Who is answering, what it costs, and where to change it — in one line.
 *
 * This replaced a six-card picker that sat above the question box on the
 * lesson player, the exam player and the speaking page. The picker worked;
 * it was simply far too much furniture for the decision it carried. On a
 * phone it was a wall the learner scrolled past before every single
 * question, and the client said so: "on mobile view it will be
 * distraction".
 *
 * What survives is the half that was actually doing work. The client's
 * original ask was that a learner can see what a choice COSTS them — "we
 * also tell them how much platform credits they will be using by choosing
 * respective LLM model" — and a setting hidden in a profile nobody opens
 * would quietly undo that. So the price stays at the point of spending,
 * and the choice itself moves to where preferences live.
 *
 * It renders nothing at all when there is nothing worth saying: fewer than
 * two partners is not a choice, and a partner we cannot price is a line
 * that would have to lie about the number that matters.
 */
import { Link } from "react-router-dom";
import { useT } from "../../i18n";
import type { AiModel } from "../../api/exams";

/** Paise → "₹0.22" / "₹2.12". Two decimals always: the cheapest partner
 *  costs a fifth of a rupee, and rounding it to "₹0" would read as free
 *  rather than cheap — losing exactly the twenty-fold difference the line
 *  exists to show. */
const perConversation = (minor: number) =>
  `₹${(minor / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export function PartnerLine({
  models,
  activeId,
}: {
  models: AiModel[];
  /** The model that would actually answer the next question — resolved by
   *  the server, which knows the whole fallback chain. */
  activeId?: string | null;
}) {
  const t = useT();

  // One partner is not a choice; without a price there is nothing to say.
  if (models.length < 2) return null;
  const active = models.find((m) => m.id === activeId);
  if (!active) return null;

  return (
    <p className="mt-2 text-xs text-muted">
      {t("partner.using", {
        name: active.label,
        price: perConversation(active.price_minor),
      })}{" "}
      <Link
        to="/profile"
        className="font-semibold text-brand-dark underline underline-offset-2"
      >
        {t("partner.change")}
      </Link>
    </p>
  );
}
